import React, { useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Forward } from '@mui/icons-material';

/**
 * Popup que antecede o chat de criação de tipo de atendimento.
 * Etapa 1 ("escolha"): criar do zero ou basear em um modelo existente.
 * Etapa 2 ("modelos"): escolher qual modelo existente usar como base.
 *
 * onConfirmar(modeloBase | null) — null = criar do zero.
 */
function SeletorCriacaoTipoAtendimento({ open, onClose, tiposResumos, onConfirmar }) {
    const theme = useTheme();
    const [etapa, setEtapa] = useState('escolha');

    useEffect(() => {
        if (open) {
            setEtapa('escolha');
        }
    }, [open]);

    function selecionarModelo(tipoResumo) {
        onConfirmar && onConfirmar(tipoResumo);
    }

    const modelos = [...(tiposResumos || [])].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    pb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {etapa === 'modelos' && (
                        <IconButton onClick={() => setEtapa('escolha')} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" fontWeight={600} color={theme.palette.primary.dark}>
                        {etapa === 'escolha' ? 'Criar novo tipo de atendimento' : 'Escolha um modelo como base'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ py: 2 }}>
                {etapa === 'escolha' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Box
                            onClick={() => onConfirmar && onConfirmar(null)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        >
                            <AddIcon color="primary" />
                            <Box>
                                <Typography variant="body1" fontWeight={600} color={theme.palette.primary.dark}>
                                    Criar do zero
                                </Typography>
                                <Typography variant="caption" color={theme.palette.text.secondary}>
                                    Monte um novo tipo de atendimento respondendo às perguntas do assistente.
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            onClick={() => setEtapa('modelos')}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        >
                            <ContentCopyIcon color="primary" />
                            <Box>
                                <Typography variant="body1" fontWeight={600} color={theme.palette.primary.dark}>
                                    Basear em um modelo existente
                                </Typography>
                                <Typography variant="caption" color={theme.palette.text.secondary}>
                                    Use um tipo de atendimento já existente como ponto de partida e ajuste o que precisar.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        {modelos.length === 0 ? (
                            <Typography variant="body2" color={theme.palette.text.secondary} sx={{ textAlign: 'center', py: 3 }}>
                                Nenhum modelo disponível para usar como base.
                            </Typography>
                        ) : (
                            modelos.map((tipoResumo) => (
                                <MenuItem
                                    key={tipoResumo.id}
                                    onClick={() => selecionarModelo(tipoResumo)}
                                    dense
                                >
                                    <Box display="flex" flexDirection="column" width="100%" overflow="hidden">
                                        <Typography variant="body1" fontWeight={600} color={theme.palette.primary.dark}>
                                            {tipoResumo.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color={theme.palette.text.disabled}
                                            sx={{ textWrap: 'initial' }}
                                        >
                                            {tipoResumo.description}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <IconButton aria-label="selecionar" color="primary">
                                            <Forward />
                                        </IconButton>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default SeletorCriacaoTipoAtendimento;
